import Phaser from 'phaser';
import { Entity, EntityConfig } from './Entity';
import { DEPTH } from '../constants';
import { AudioRouter } from '../systems/AudioRouter';

// ============================================
// Drop Entity (Coins and Gems on the ground)
// Based on economy_coins_gems_pickups.md
// ============================================

export type DropType = 'coin' | 'gem';

export interface DropConfig extends Omit<EntityConfig, 'type'> {
  dropType: DropType;
  value: number;
  velocity?: { x: number; y: number };
  lifetime?: number;  // Seconds before despawn
  source?: 'monster' | 'tree' | 'player_spill' | 'tower_sell';
  ownerPlayerId?: string;  // For player_spill: spiller can't pick up for 500ms
}

const DROP_COLORS: Record<DropType, number> = {
  coin: 0xffd700,  // Gold
  gem: 0xff44ff,   // Purple/pink
};

const DROP_SIZE: Record<DropType, number> = {
  coin: 8,
  gem: 10,
};

const SPILL_PICKUP_DELAY_MS = 500;

export class Drop extends Entity {
  public readonly dropType: DropType;
  public readonly value: number;
  public readonly source: string;
  public readonly ownerPlayerId: string | undefined;

  // Physics
  private velocityX: number;
  private velocityY: number;
  private friction: number = 0.92;

  // Lifetime
  private lifetime: number;
  private spawnTime: number;
  private magnetizable: boolean = true;
  private collected: boolean = false;

  // Visuals
  private body: Phaser.GameObjects.Arc | Phaser.GameObjects.Image;
  private shine: Phaser.GameObjects.Arc | null = null;
  private valueText: Phaser.GameObjects.Text | null = null;
  private useImage: boolean = false;

  // Coin animation
  private coinAnimFrames: string[] = ['coin-2', 'coin-3', 'coin-4'];
  private coinAnimIndex: number = 0;
  private coinAnimTimer: number = 0;
  private readonly COIN_ANIM_INTERVAL: number = 100; // ms between frames

  // Ghost sprite for see-through visibility (2.5D)
  private ghostSprite: Phaser.GameObjects.Arc | Phaser.GameObjects.Image | null = null;

  // Events
  public onCollected?: (value: number, dropType: DropType) => void;
  public onExpired?: () => void;

  constructor(scene: Phaser.Scene, config: DropConfig) {
    super(scene, { ...config, type: 'drop' });

    this.dropType = config.dropType;
    this.value = config.value;
    this.source = config.source || 'monster';
    this.ownerPlayerId = config.ownerPlayerId;
    this.velocityX = config.velocity?.x || 0;
    this.velocityY = config.velocity?.y || 0;
    this.lifetime = (config.lifetime || 15) * 1000; // Convert to ms
    this.spawnTime = Date.now();

    // Create visuals - use images if available
    const size = DROP_SIZE[this.dropType];
    const color = DROP_COLORS[this.dropType];

    if (this.dropType === 'coin' && scene.textures.exists('coin-2')) {
      // Use animated coin images
      this.useImage = true;
      const img = scene.add.image(0, 0, 'coin-2');
      img.setScale(0.18);
      this.body = img;
      this.container.add(this.body);
    } else if (this.dropType === 'gem' && scene.textures.exists('gem-1')) {
      // Use gem image
      this.useImage = true;
      const img = scene.add.image(0, 0, 'gem-1');
      img.setScale(0.18);
      this.body = img;
      this.container.add(this.body);
      
      // Show value for high-value gems
      if (this.value > 1) {
        this.valueText = scene.add.text(0, -12, `${this.value}`, {
          fontSize: '10px',
          color: '#ffffff',
          fontStyle: 'bold',
          stroke: '#000000',
          strokeThickness: 2,
        });
        this.valueText.setOrigin(0.5);
        this.container.add(this.valueText);
      }
    } else {
      // Fallback to circles
      this.body = scene.add.circle(0, 0, size, color);
      (this.body as Phaser.GameObjects.Arc).setStrokeStyle(2, 0xffffff);
      this.container.add(this.body);

      // Add shine effect for gems
      if (this.dropType === 'gem') {
        this.shine = scene.add.circle(0, 0, size - 2, 0xffffff, 0.3);
        this.container.add(this.shine);

        // Show value for high-value gems
        if (this.value > 1) {
          this.valueText = scene.add.text(0, -size - 6, `${this.value}`, {
            fontSize: '10px',
            color: '#ffffff',
            fontStyle: 'bold',
          });
          this.valueText.setOrigin(0.5);
          this.container.add(this.valueText);
        }
      }
    }

    // Y-based depth for 2.5D sorting
    this.updateDepth();

    // Create ghost sprite for see-through visibility
    this.createGhostSprite();

    // Start with a small bounce animation
    this.scene.tweens.add({
      targets: this.container,
      scaleX: 1.2,
      scaleY: 0.8,
      duration: 100,
      yoyo: true,
    });
  }

  /**
   * Create a semi-transparent ghost sprite on a high layer for visibility
   */
  private createGhostSprite(): void {
    const size = DROP_SIZE[this.dropType];
    const color = DROP_COLORS[this.dropType];
    
    if (this.useImage) {
      const textureKey = this.dropType === 'coin' ? 'coin-2' : 'gem-1';
      if (this.scene.textures.exists(textureKey)) {
        this.ghostSprite = this.scene.add.image(this._x, this._y, textureKey);
        (this.ghostSprite as Phaser.GameObjects.Image).setScale(0.18);
      }
    }
    
    if (!this.ghostSprite) {
      this.ghostSprite = this.scene.add.circle(this._x, this._y, size, color);
    }
    
    this.ghostSprite.setAlpha(0.4);
    this.ghostSprite.setDepth(DEPTH.GHOST_LAYER);
  }

  /** Bottom of drop visual for depth sorting (lower on screen = in front) */
  protected getDepthY(): number {
    return Phaser.Display.Bounds.GetBottom(this.body);
  }

  update(delta: number): void {
    if (!this.active || this.destroyed || this.collected) return;

    // Apply velocity (velocity is in pixels per second — frame-rate independent).
    // Always apply movement so magnet pull works even when velocity is small (was: only if > 0.5, which broke gravitation).
    const dt = delta / 1000;
    this._x += this.velocityX * dt;
    this._y += this.velocityY * dt;

    // Bounce off screen edges (only when we had meaningful velocity)
    const cam = this.scene.cameras.main;
    const padding = 20;
    const minX = cam.scrollX + padding;
    const maxX = cam.scrollX + cam.width - padding;
    const minY = cam.scrollY + padding;
    const maxY = cam.scrollY + cam.height - padding;

    if (this._x < minX) {
      this._x = minX;
      this.velocityX = Math.abs(this.velocityX) * 0.6; // Bounce with damping
    } else if (this._x > maxX) {
      this._x = maxX;
      this.velocityX = -Math.abs(this.velocityX) * 0.6;
    }

    if (this._y < minY) {
      this._y = minY;
      this.velocityY = Math.abs(this.velocityY) * 0.6;
    } else if (this._y > maxY) {
      this._y = maxY;
      this.velocityY = -Math.abs(this.velocityY) * 0.6;
    }

    this.container.setPosition(this._x, this._y);

    // Apply friction; snap to zero only when truly negligible (avoids killing magnet pull)
    this.velocityX *= this.friction;
    this.velocityY *= this.friction;
    const speed = Math.sqrt(this.velocityX * this.velocityX + this.velocityY * this.velocityY);
    if (speed < 0.01) {
      this.velocityX = 0;
      this.velocityY = 0;
    }

    // Coin spinning animation
    if (this.useImage && this.dropType === 'coin' && this.scene.textures.exists('coin-2')) {
      this.coinAnimTimer += delta;
      if (this.coinAnimTimer >= this.COIN_ANIM_INTERVAL) {
        this.coinAnimTimer = 0;
        this.coinAnimIndex = (this.coinAnimIndex + 1) % this.coinAnimFrames.length;
        (this.body as Phaser.GameObjects.Image).setTexture(this.coinAnimFrames[this.coinAnimIndex]);
      }
    }

    // Check lifetime
    const elapsed = Date.now() - this.spawnTime;
    if (elapsed >= this.lifetime) {
      this.expire();
      return;
    }

    // Blink near end of life (last 3 seconds)
    if (elapsed >= this.lifetime - 3000) {
      const blinkRate = 100 + (this.lifetime - elapsed) / 30;
      this.container.setAlpha(Math.sin(elapsed / blinkRate) > 0 ? 1 : 0.3);
    }

    // Gem shine animation (only for circle fallback)
    if (this.shine) {
      this.shine.setScale(0.8 + Math.sin(elapsed / 200) * 0.2);
    }

    // Update depth based on Y position
    this.updateDepth();

    // Sync ghost sprite
    this.syncGhostSprite();
  }

  /**
   * Sync ghost sprite with main sprite position and texture
   */
  private syncGhostSprite(): void {
    if (!this.ghostSprite) return;
    
    this.ghostSprite.setPosition(this._x, this._y);
    
    // Sync texture for animated coins
    if (this.useImage && this.dropType === 'coin' && this.ghostSprite instanceof Phaser.GameObjects.Image) {
      this.ghostSprite.setTexture(this.coinAnimFrames[this.coinAnimIndex]);
    }
  }

  /**
   * Apply magnetic force toward a player.
   * strength = speed (px/s) to add toward the player this frame (frame-rate independent when caller multiplies by delta/1000).
   */
  applyMagnetForce(playerX: number, playerY: number, strength: number): void {
    if (!this.magnetizable || this.collected) return;

    const dx = playerX - this._x;
    const dy = playerY - this._y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist > 0) {
      // Add velocity toward player; magnitude = strength (no extra 1/dist so pull is strong and consistent)
      this.velocityX += (dx / dist) * strength;
      this.velocityY += (dy / dist) * strength;
    }
  }

  /**
   * Collect this drop
   */
  collect(): void {
    if (this.collected || this.destroyed) return;

    this.collected = true;
    this.magnetizable = false;

    // Play collection sound (throttled - coins only)
    if (this.dropType === 'coin') {
      AudioRouter.playCoinCollect();
    }

    // Callback
    this.onCollected?.(this.value, this.dropType);

    // Collection effect
    this.showCollectEffect();

    // Destroy after effect
    this.scene.time.delayedCall(200, () => {
      this.destroy();
    });
  }

  /**
   * Drop expired without being collected
   */
  private expire(): void {
    if (this.destroyed) return;

    // Fade out
    this.scene.tweens.add({
      targets: this.container,
      alpha: 0,
      scale: 0,
      duration: 200,
      onComplete: () => {
        this.onExpired?.();
        this.destroy();
      },
    });
  }

  private showCollectEffect(): void {
    const x = this._x;
    const y = this._y;
    const color = DROP_COLORS[this.dropType];

    // Quick scale up and fade
    this.scene.tweens.add({
      targets: this.container,
      scaleX: 1.5,
      scaleY: 1.5,
      alpha: 0,
      y: y - 20,
      duration: 200,
    });

    // Sparkle particles
    for (let i = 0; i < 4; i++) {
      const particle = this.scene.add.circle(x, y, 3, color);
      particle.setDepth(100);

      const angle = (i / 4) * Math.PI * 2;
      this.scene.tweens.add({
        targets: particle,
        x: x + Math.cos(angle) * 15,
        y: y + Math.sin(angle) * 15,
        alpha: 0,
        scale: 0,
        duration: 200,
        onComplete: () => particle.destroy(),
      });
    }

    // Value text popup
    const text = this.scene.add.text(x - 10, y - 15, `+${this.value}`, {
      fontSize: '12px',
      color: this.dropType === 'gem' ? '#ff88ff' : '#ffd700',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2,
    });
    text.setOrigin(0.5);
    text.setDepth(100);

    // Add small icon next to value
    const iconKey = this.dropType === 'gem' ? 'gem-1' : 'coin-1';
    if (this.scene.textures.exists(iconKey)) {
      const icon = this.scene.add.image(x + 16, y - 15, iconKey);
      icon.setScale(0.09);
      icon.setDepth(100);
      
      this.scene.tweens.add({
        targets: icon,
        y: y - 40,
        alpha: 0,
        duration: 600,
        onComplete: () => icon.destroy(),
      });
    }

    this.scene.tweens.add({
      targets: text,
      y: y - 40,
      alpha: 0,
      duration: 600,
      onComplete: () => text.destroy(),
    });
  }

  // Getters
  getVelocity(): { x: number; y: number } {
    return { x: this.velocityX, y: this.velocityY };
  }

  isMagnetizable(): boolean {
    return this.magnetizable && !this.collected;
  }

  isCollected(): boolean {
    return this.collected;
  }

  getRemainingLifetime(): number {
    return Math.max(0, this.lifetime - (Date.now() - this.spawnTime));
  }

  /**
   * Whether this drop can be collected by the given player.
   * Spiller cannot pick up their own spilled coins for 500ms.
   */
  canBeCollectedBy(playerId: string): boolean {
    if (this.source !== 'player_spill' || !this.ownerPlayerId) return true;
    if (this.ownerPlayerId !== playerId) return true;
    return Date.now() - this.spawnTime >= SPILL_PICKUP_DELAY_MS;
  }

  /**
   * Clean up ghost sprite on destroy
   */
  destroy(): void {
    if (this.ghostSprite) {
      this.ghostSprite.destroy();
      this.ghostSprite = null;
    }
    super.destroy();
  }
}
